import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

interface TimeLeft {
  minutos: string;
  segundos: string;
}

function calcularTempoRestante(): TimeLeft {
  const agora = new Date();
  const meiaNiote = new Date();
  meiaNiote.setHours(24, 0, 0, 0);
  const diff = Math.max(0, meiaNiote.getTime() - agora.getTime());
  const totalSegundos = Math.floor(diff / 1000);
  const minutos = Math.floor((totalSegundos % 3600) / 60);
  const segundos = totalSegundos % 60;
  return {
    minutos: String(minutos).padStart(2, '0'),
    segundos: String(segundos).padStart(2, '0'),
  };
}

const STORAGE_KEY = 'bia_exit_popup_shown';

export default function ExitIntentLinks() {
  const [visivel, setVisivel] = useState(false);
  const [fechando, setFechando] = useState(false);
  const [tempo, setTempo] = useState<TimeLeft>(calcularTempoRestante());
  const [pulsar, setPulsar] = useState(false);
  const [jaExibido, setJaExibido] = useState(false);

  const fechar = useCallback(() => {
    setFechando(true);
    setTimeout(() => {
      setVisivel(false);
      setFechando(false);
    }, 300);
    sessionStorage.setItem(STORAGE_KEY, '1');
  }, []);

  const abrir = useCallback(() => {
    if (jaExibido || sessionStorage.getItem(STORAGE_KEY)) return;
    setVisivel(true);
    setJaExibido(true);
  }, [jaExibido]);

  // Desktop: mouse sai pelo topo
  useEffect(() => {
    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 5) {
        abrir();
      }
    };
    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, [abrir]);

  // Mobile: troca de aba / minimizar
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        abrir();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [abrir]);

  // Timer de contagem regressiva
  useEffect(() => {
    if (!visivel) return;
    const interval = setInterval(() => {
      setTempo(calcularTempoRestante());
      setPulsar(true);
      setTimeout(() => setPulsar(false), 300);
    }, 1000);
    return () => clearInterval(interval);
  }, [visivel]);

  // Fechar com ESC
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && visivel) fechar();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [visivel, fechar]);

  if (!visivel) return null;

  return (
    <div
      className={`fixed inset-0 z-[999] flex items-end sm:items-center justify-center p-4 transition-all duration-300 ${
        fechando ? 'opacity-0' : 'opacity-100'
      }`}
      onClick={fechar}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" />

      {/* Modal */}
      <div
        className={`relative w-full max-w-sm bg-gradient-to-br from-[#1a1610] via-charcoal-900 to-[#12110e] rounded-3xl border border-gold-500/40 overflow-hidden transition-all duration-300 ${
          fechando ? 'translate-y-4 scale-95 opacity-0' : 'translate-y-0 scale-100 opacity-100'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Glow topo */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold-500/70 to-transparent" />
        {/* Glow BG */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(201,164,64,0.10)_0%,_transparent_65%)] pointer-events-none" />

        {/* Botão fechar */}
        <button
          onClick={fechar}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/8 border border-white/10 text-white/40 hover:text-white/70 hover:bg-white/15 transition-all cursor-pointer z-10"
        >
          <i className="ri-close-line text-base" />
        </button>

        <div className="relative p-6">

          {/* Emoji + badge */}
          <div className="flex items-center gap-2 mb-4">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-gold-500/15 border border-gold-500/30">
              <i className="ri-alarm-warning-line text-xl text-gold-400" />
            </div>
            <div className="flex flex-col">
              <span className="text-[9px] font-bold text-gold-400/70 uppercase tracking-[0.2em]">Espera!</span>
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-gold-400 animate-pulse" />
                <span className="text-[10px] text-gold-300 font-semibold">Oferta ainda ativa</span>
              </div>
            </div>
          </div>

          {/* Título */}
          <h2 className="font-display text-xl font-bold text-white leading-snug mb-2">
            Você está deixando<br />
            <span className="text-gold-400">67% de desconto</span> na mesa
          </h2>
          <p className="text-sm text-white/50 leading-relaxed mb-5">
            Essa oferta foi criada especialmente para seguidoras do Instagram. Depois que você sair, o preço volta ao normal.
          </p>

          {/* Preço */}
          <div className="flex items-end gap-3 bg-black/25 border border-gold-500/20 rounded-2xl px-4 py-3 mb-4">
            <div>
              <p className="text-xs text-white/30 line-through mb-0.5">De R$ 297/mês</p>
              <div className="flex items-baseline gap-1">
                <span className="text-xs text-gold-400 font-semibold">Por</span>
                <span className="font-display text-3xl font-bold text-gold-300 leading-none">R$ 97</span>
                <span className="text-xs text-white/35">/mês</span>
              </div>
            </div>
            <div className="ml-auto text-right">
              <span className="inline-block text-xs font-bold text-emerald-400 bg-emerald-400/10 border border-emerald-400/20 px-2.5 py-1 rounded-full whitespace-nowrap">
                ECONOMIZE R$ 200
              </span>
            </div>
          </div>

          {/* Contador compacto */}
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-1.5">
              <i className="ri-time-line text-white/25 text-xs" />
              <span className="text-[11px] text-white/35 font-medium">Desconto expira em</span>
            </div>
            <div className="flex items-center gap-1">
              <div className={`px-2.5 py-1.5 rounded-lg bg-gold-500/12 border border-gold-500/25 transition-all ${pulsar ? 'bg-gold-500/20' : ''}`}>
                <span className="font-display text-base font-bold text-gold-300 tabular-nums leading-none">
                  {tempo.minutos}
                </span>
              </div>
              <span className="text-gold-500/60 font-bold text-sm">:</span>
              <div className={`px-2.5 py-1.5 rounded-lg bg-gold-500/12 border border-gold-500/25 transition-all ${pulsar ? 'bg-gold-500/20 border-gold-400/40' : ''}`}>
                <span className="font-display text-base font-bold text-gold-300 tabular-nums leading-none">
                  {tempo.segundos}
                </span>
              </div>
              <span className="text-[10px] text-white/25 ml-1 font-medium">hoje</span>
            </div>
          </div>

          {/* O que inclui — resumido */}
          <div className="grid grid-cols-2 gap-1.5 mb-5">
            {[
              { icon: 'ri-headphone-line', text: 'Áudios guiados' },
              { icon: 'ri-file-text-line', text: 'Workbooks PDF' },
              { icon: 'ri-customer-service-2-line', text: 'Suporte direto' },
              { icon: 'ri-infinity-line', text: 'Acesso vitalício' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2 bg-white/[0.03] border border-white/[0.06] rounded-xl px-3 py-2">
                <i className={`${item.icon} text-gold-500/60 text-xs`} />
                <span className="text-[11px] text-white/40 font-medium leading-tight">{item.text}</span>
              </div>
            ))}
          </div>

          {/* CTAs */}
          <Link
            to="/planos"
            onClick={fechar}
            className="group flex items-center justify-center gap-2 w-full py-3.5 rounded-xl bg-gradient-to-r from-gold-600 to-gold-500 hover:from-gold-700 hover:to-gold-600 text-charcoal-900 font-bold text-sm tracking-wide transition-all cursor-pointer whitespace-nowrap mb-2"
          >
            <i className="ri-vip-crown-line text-base" />
            Quero o desconto agora
            <i className="ri-arrow-right-line text-sm group-hover:translate-x-0.5 transition-transform" />
          </Link>

          <button
            onClick={fechar}
            className="w-full py-2.5 text-xs text-white/20 hover:text-white/40 transition-colors cursor-pointer"
          >
            Não, prefiro pagar o preço cheio depois
          </button>

          {/* Garantia */}
          <div className="flex items-center justify-center gap-1.5 mt-1">
            <i className="ri-shield-check-line text-white/20 text-xs" />
            <span className="text-[10px] text-white/20">Garantia de 7 dias — risco zero</span>
          </div>
        </div>

        {/* Glow rodapé */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold-500/30 to-transparent" />
      </div>
    </div>
  );
}
